import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { formatDateTime } from '@/lib/format';
import Modal from '@/components/base/Modal';
import type { Client } from '@/lib/types';

export default function ClientsPage() {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase.from('clients').select('*').order('name');
      if (e) throw e;
      setClients((data as Client[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const { error: e } = await supabase.from('clients').insert({
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
      });
      if (e) throw e;
      setModal(false);
      setName('');
      setPhone('');
      setEmail('');
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error: e } = await supabase.from('clients').delete().eq('id', deleteTarget.id);
    if (e) {
      setError(e.message);
      return;
    }
    setDeleteTarget(null);
    load();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Clientes</h1>
          <p className="mt-1 text-foreground-600">Registro de clientes del negocio.</p>
        </div>
        <button
          onClick={() => setModal(true)}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-user-add-line" /> Nuevo cliente
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : clients.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-user-3-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Aún no hay clientes registrados.</p>
        </div>
      ) : (
        <div className="mt-6 rounded-lg border border-background-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-background-100 text-foreground-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Nombre</th>
                <th className="px-4 py-3 font-medium">Teléfono</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">Email</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Registro</th>
                {isAdmin && <th className="px-4 py-3 font-medium text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {clients.map((client) => (
                <tr key={client.id} className="hover:bg-background-100 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center font-semibold shrink-0">
                        {client.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-foreground-950">{client.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{client.phone || '—'}</td>
                  <td className="px-4 py-3 text-foreground-700 hidden sm:table-cell">{client.email || '—'}</td>
                  <td className="px-4 py-3 text-foreground-600 hidden md:table-cell">
                    {formatDateTime(client.created_at)}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setDeleteTarget(client)}
                        className="w-8 h-8 rounded-md inline-flex items-center justify-center text-foreground-400 hover:text-accent-700 hover:bg-accent-50 cursor-pointer"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-6-line" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Agregar cliente */}
      <Modal open={modal} onClose={() => setModal(false)} title="Nuevo cliente">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Nombre *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Teléfono</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={saving || !name.trim()}
            className="w-full rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            {saving ? 'Guardando...' : 'Guardar cliente'}
          </button>
        </div>
      </Modal>

      {/* Eliminar cliente */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar cliente"
        footer={
          <>
            <button
              onClick={() => setDeleteTarget(null)}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 transition whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleDelete}
              className="rounded-md bg-accent-500 hover:bg-accent-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              Eliminar
            </button>
          </>
        }
      >
        ¿Seguro que deseas eliminar a <strong>{deleteTarget?.name}</strong>?
      </Modal>
    </div>
  );
}